import type { FishStockLog } from '../types/production'

import { ensureOfflineReady } from '../offline/initOffline'
import { getActiveTenantKey } from '../offline/tenant'
import { enqueueEntityChange, enqueueEntityChanges } from '../offline/outbox/entityOutbox'
import { getOfflineDb } from '../offline/dbFactory'
import {
  dexieFishStockLogRepo,
  type FishStockLogListParams
} from '../offline/repos/DexieFishStockLogRepo'
import { normalizeToIsoZ } from './dateNormalization'
import {
  buildQueuedEntityMutationResult,
  readEntityBatchSyncStatus,
  retryEntityBatchSyncStatusWithReport,
  type EntityBatchSyncRetryReport,
  type EntityBatchSyncStatus,
  type EntityMutationResult
} from './entityMutationSync'
import { createId } from './id'
import { api } from './api'

const nowIso = (): string => new Date().toISOString()

const toOptionalTrimmed = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

const normalizeCountedStock = (value: unknown): number => {
  const raw = typeof value === 'number' ? value : Number(String(value ?? '').replaceAll(',', '').trim())
  if (!Number.isFinite(raw)) {
    throw new Error('Counted stock must be numeric')
  }
  if (!Number.isInteger(raw)) {
    throw new Error('Counted stock must be a whole number')
  }
  if (raw < 0) {
    throw new Error('Counted stock must be zero or greater')
  }
  if (raw > 10_000_000) {
    throw new Error('Counted stock must be 10,000,000 or below')
  }
  return raw
}

const normalizeMethod = (value: unknown): 'count' | 'estimation' => {
  if (value === 'count' || value === 'estimation') return value
  throw new Error("Method must be 'count' or 'estimation'")
}

const normalizeRecordedAt = (raw?: string): string => {
  if (!raw) return nowIso()
  const normalized = normalizeToIsoZ(raw)
  if (normalized.ok) return normalized.isoZ
  return nowIso()
}

const isOnline = (): boolean => {
  if (typeof navigator === 'undefined') return true
  return navigator.onLine
}

const REMOTE_LIST_TTL_MS = 30_000
const lastRemoteListAtByKey = new Map<string, number>()

const buildRemoteListKey = (params: FishStockLogListParams): string => {
  const clean = {
    tenantKey: getActiveTenantKey(),
    farmId: params.farmId?.trim() || '',
    productionUnitId: params.productionUnitId?.trim() || '',
    lot: params.lot?.trim() || '',
    lotId: params.lotId?.trim() || '',
    productionRowId: params.productionRowId?.trim() || '',
    fromRecordedAt: params.fromRecordedAt?.trim() || '',
    toRecordedAt: params.toRecordedAt?.trim() || ''
  }
  return JSON.stringify(clean)
}

const shouldAttemptRemoteList = (params: FishStockLogListParams): boolean => {
  if (!isOnline()) return false
  const key = buildRemoteListKey(params)
  const lastAt = lastRemoteListAtByKey.get(key)
  if (!lastAt) return true
  return Date.now() - lastAt > REMOTE_LIST_TTL_MS
}

const getPendingFishStockLogIds = async (): Promise<Set<string>> => {
  const entries = await getOfflineDb().entityOutbox.toArray()
  return new Set(
    entries
      .filter((entry) => entry.entityType === 'fishStockLog')
      .map((entry) => entry.entityId)
  )
}

const hasPendingFishStockLogOutboxEntry = async (entityId: string): Promise<boolean> => {
  const trimmed = entityId.trim()
  if (!trimmed) return false

  try {
    const db = getOfflineDb()
    const match = await db.entityOutbox
      .where('[entityType+entityId]')
      .equals(['fishStockLog', trimmed])
      .first()
    return Boolean(match)
  } catch {
    return false
  }
}

const pruneRemovedRemoteFishStockLogs = async (
  cached: FishStockLog[],
  remoteMapped: FishStockLog[]
): Promise<void> => {
  const remoteIds = new Set(remoteMapped.map((log) => log.id))

  for (const log of cached) {
    if (remoteIds.has(log.id)) continue
    // Preserve local-only changes until their outbox entry is flushed.
    // eslint-disable-next-line no-await-in-loop
    const hasPending = await hasPendingFishStockLogOutboxEntry(log.id)
    if (hasPending) continue
    // eslint-disable-next-line no-await-in-loop
    await dexieFishStockLogRepo.delete(log.id)
  }
}

const normalizeRemoteFishStockLog = (candidate: any): FishStockLog | null => {
  if (!candidate || typeof candidate !== 'object') return null
  const id = typeof candidate.id === 'string' ? candidate.id.trim() : ''
  const farmId = typeof candidate.farmId === 'string' ? candidate.farmId.trim() : ''
  const productionUnitId =
    typeof candidate.productionUnitId === 'string' ? candidate.productionUnitId.trim() : ''
  const recordedAtRaw = typeof candidate.recordedAt === 'string' ? candidate.recordedAt.trim() : ''
  if (!id || !farmId || !productionUnitId || !recordedAtRaw) return null

  try {
    return {
      id,
      farmId,
      productionUnitId,
      lot: typeof candidate.lot === 'string' ? candidate.lot.trim() || undefined : undefined,
      lotId: typeof candidate.lotId === 'string' ? candidate.lotId.trim() || undefined : undefined,
      productionRowId:
        typeof candidate.productionRowId === 'string' ? candidate.productionRowId.trim() || undefined : undefined,
      recordedAt: normalizeRecordedAt(recordedAtRaw),
      countedStock: normalizeCountedStock(candidate.countedStock),
      method: normalizeMethod(candidate.method),
      notes: toOptionalTrimmed(candidate.notes),
      metadata:
        typeof candidate.metadata === 'object' && candidate.metadata !== null ? candidate.metadata : undefined,
      createdAt: typeof candidate.createdAt === 'string' ? candidate.createdAt : undefined,
      updatedAt: typeof candidate.updatedAt === 'string' ? candidate.updatedAt : undefined
    }
  } catch {
    return null
  }
}

export type FishStockLogCreatePayload = {
  id?: string
  farmId: string
  productionUnitId: string
  lot?: string
  lotId?: string
  productionRowId?: string
  recordedAt?: string
  countedStock: number | string
  method: 'count' | 'estimation'
  notes?: string
  metadata?: Record<string, unknown>
}

export type FishStockLogUpdatePayload = Partial<{
  lot: string | null
  lotId: string | null
  productionRowId: string | null
  recordedAt: string
  countedStock: number | string
  method: 'count' | 'estimation'
  notes: string | null
  metadata: Record<string, unknown> | null
}>

export type FishStockLogMutationResult = EntityMutationResult<FishStockLog>
export type FishStockLogDeleteResult = EntityMutationResult<void>

const normalizeFishStockLogRecord = (args: {
  candidate: FishStockLogCreatePayload | FishStockLog
  id?: string
  createdAt?: string
  updatedAt?: string
}): FishStockLog => {
  const farmId = toOptionalTrimmed(args.candidate.farmId)
  if (!farmId) throw new Error('Facility is required')

  const productionUnitId = toOptionalTrimmed(args.candidate.productionUnitId)
  if (!productionUnitId) throw new Error('Production unit is required')

  const timestamp = nowIso()
  const candidateId =
    typeof (args.candidate as FishStockLog).id === 'string'
      ? (args.candidate as FishStockLog).id.trim()
      : ''

  return {
    id: args.id?.trim() || candidateId || createId('fsl'),
    farmId,
    productionUnitId,
    lot: toOptionalTrimmed(args.candidate.lot),
    lotId: toOptionalTrimmed(args.candidate.lotId),
    productionRowId: toOptionalTrimmed(args.candidate.productionRowId),
    recordedAt: normalizeRecordedAt(args.candidate.recordedAt),
    countedStock: normalizeCountedStock(args.candidate.countedStock),
    method: normalizeMethod(args.candidate.method),
    notes: toOptionalTrimmed(args.candidate.notes),
    metadata: args.candidate.metadata,
    createdAt: args.createdAt?.trim() || timestamp,
    updatedAt: args.updatedAt?.trim() || timestamp
  }
}

export const fishStockLogApi = {
  readFishStockLogSyncStatus: async (logIds: string[]): Promise<EntityBatchSyncStatus> => {
    return readEntityBatchSyncStatus('fishStockLog', logIds)
  },

  retryFishStockLogSyncStatusWithReport: async (
    logIds: string[]
  ): Promise<EntityBatchSyncRetryReport> => {
    return retryEntityBatchSyncStatusWithReport({
      entityType: 'fishStockLog',
      entityIds: logIds
    })
  },

  list: async (
    params: FishStockLogListParams = {},
    options?: { preferLocal?: boolean; forceRemote?: boolean }
  ): Promise<FishStockLog[]> => {
    await ensureOfflineReady()

    const [cached, pendingIds] = await Promise.all([
      dexieFishStockLogRepo.list(params),
      getPendingFishStockLogIds()
    ])
    const shouldFetchRemote = options?.forceRemote ? isOnline() : shouldAttemptRemoteList(params)

    // Same TTL-based pattern as weightSampleLogApi / mortalityLogApi
    // (finding #57): `preferLocal` remains in the signature for back-compat
    // but freshness is decided by the `REMOTE_LIST_TTL_MS` gate so
    // peer-device events surface after the TTL window even when the caller
    // opted in to prefer-local.
    if (!shouldFetchRemote) {
      return cached
    }

    const key = buildRemoteListKey(params)
    try {
      const remote = await api.fishStockLogs.list({
        farmId: params.farmId,
        productionUnitId: params.productionUnitId,
        lot: params.lot,
        lotId: params.lotId,
        productionRowId: params.productionRowId,
        fromRecordedAt: params.fromRecordedAt,
        toRecordedAt: params.toRecordedAt,
        noCache: options?.forceRemote
      })

      const mapped = remote
        .map(normalizeRemoteFishStockLog)
        .filter((log): log is FishStockLog => Boolean(log))
        .filter((log) => !pendingIds.has(log.id))

      await pruneRemovedRemoteFishStockLogs(cached, mapped)
      await dexieFishStockLogRepo.upsertMany(mapped)
      lastRemoteListAtByKey.set(key, Date.now())

      return dexieFishStockLogRepo.list(params)
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[fishStockLogApi] remote list failed; falling back to cache', error)
      }
      if (cached.length > 0) return cached
      throw error
    }
  },

  get: async (id: string): Promise<FishStockLog | undefined> => {
    await ensureOfflineReady()
    return dexieFishStockLogRepo.getById(id)
  },

  create: async (payload: FishStockLogCreatePayload): Promise<FishStockLogMutationResult> => {
    await ensureOfflineReady()
    const timestamp = nowIso()
    const log = normalizeFishStockLogRecord({
      candidate: payload,
      id: payload.id,
      createdAt: timestamp,
      updatedAt: timestamp
    })

    await dexieFishStockLogRepo.upsert(log)
    await enqueueEntityChange({
      entityType: 'fishStockLog',
      op: 'upsert',
      entityId: log.id,
      payload: {
        ...log,
        id: log.id
      }
    })

    return buildQueuedEntityMutationResult(log, {
      entityType: 'fishStockLog',
      entityId: log.id
    })
  },

  saveImportedLogs: async (logs: FishStockLog[]): Promise<FishStockLog[]> => {
    await ensureOfflineReady()
    if (logs.length === 0) return []

    const timestamp = nowIso()
    const normalizedLogs = logs.map((log) =>
      normalizeFishStockLogRecord({
        candidate: log,
        createdAt: typeof log.createdAt === 'string' ? log.createdAt : timestamp,
        updatedAt: timestamp
      })
    )

    await dexieFishStockLogRepo.upsertMany(normalizedLogs)
    await enqueueEntityChanges(
      normalizedLogs.map((log) => ({
        entityType: 'fishStockLog' as const,
        op: 'upsert' as const,
        entityId: log.id,
        payload: {
          ...log,
          id: log.id
        }
      }))
    )

    return normalizedLogs
  },

  update: async (
    id: string,
    patch: FishStockLogUpdatePayload
  ): Promise<FishStockLogMutationResult> => {
    await ensureOfflineReady()

    const existing = await dexieFishStockLogRepo.getById(id)
    if (!existing) throw new Error('Fish stock log not found')

    const next: FishStockLog = {
      ...existing,
      lot: Object.prototype.hasOwnProperty.call(patch, 'lot')
        ? toOptionalTrimmed(patch.lot ?? undefined)
        : existing.lot,
      lotId: Object.prototype.hasOwnProperty.call(patch, 'lotId')
        ? toOptionalTrimmed(patch.lotId ?? undefined)
        : existing.lotId,
      productionRowId: Object.prototype.hasOwnProperty.call(patch, 'productionRowId')
        ? toOptionalTrimmed(patch.productionRowId ?? undefined)
        : existing.productionRowId,
      recordedAt: patch.recordedAt ? normalizeRecordedAt(patch.recordedAt) : existing.recordedAt,
      countedStock: Object.prototype.hasOwnProperty.call(patch, 'countedStock')
        ? normalizeCountedStock(patch.countedStock)
        : existing.countedStock,
      method: Object.prototype.hasOwnProperty.call(patch, 'method')
        ? normalizeMethod(patch.method)
        : existing.method,
      notes: Object.prototype.hasOwnProperty.call(patch, 'notes')
        ? toOptionalTrimmed(patch.notes ?? undefined)
        : existing.notes,
      metadata: Object.prototype.hasOwnProperty.call(patch, 'metadata')
        ? (patch.metadata ?? undefined)
        : existing.metadata,
      updatedAt: nowIso()
    }

    await dexieFishStockLogRepo.upsert(next)
    await enqueueEntityChange({
      entityType: 'fishStockLog',
      op: 'upsert',
      entityId: next.id,
      payload: {
        ...next,
        id: next.id
      }
    })

    return buildQueuedEntityMutationResult(next, {
      entityType: 'fishStockLog',
      entityId: next.id
    })
  },

  delete: async (id: string): Promise<FishStockLogDeleteResult> => {
    await ensureOfflineReady()

    const existing = await dexieFishStockLogRepo.getById(id)

    await dexieFishStockLogRepo.delete(id)
    await enqueueEntityChange({
      entityType: 'fishStockLog',
      op: 'delete',
      entityId: id,
      payload: existing ? { ...existing } : undefined
    })

    return buildQueuedEntityMutationResult(undefined, {
      entityType: 'fishStockLog',
      entityId: id
    })
  }
}
